import { api } from '@setplex/tria-api'
import { combine, createEvent, createStore, restore, sample } from 'effector'
import { and, combineEvents, not, or } from 'patronum'
import { options } from '~/core/flags'
import { register } from '~/core/repository'
import { model as router } from '~/core/router'
import { model as bannersModel } from '~/entities/banners'
import { model as tvModel } from '~/entities/tv'
import { model as tvBundlesModel } from '~/entities/tv/bundles'
import { model as tvCategoriesModel } from '~/entities/tv/categories'
import { model as channelsModel } from '~/entities/tv/channels'
import { model as tvFeaturedModel } from '~/entities/tv/featured'
import { genPagesLogic } from '~/shared/effector'
import { noop } from '~/shared/helpers'
import '~/shared/ui/translations/general-translations'

const DEFAULT_ROW_ELEMENTS_COUNT = 36
const DEFAULT_ROWS_COUNT = 9

export const init = createEvent<{ query: URLSearchParams }>()
export const left = createEvent()

export const load = createEvent<{
  page: number
  count?: number
}>()

/** Skeleton start*/
export const setContentLoading = createEvent<boolean>()

export const $contentLoading = restore(setContentLoading, false)

/** Skeleton end*/

// CONNECTIONS

sample({
  clock: init,
  fn: ({ query }) => ({
    page: Number(query.get('page')) || 0,
    count: Number(query.get('count')) || DEFAULT_ROWS_COUNT,
  }),
  target: load,
})

sample({
  clock: load,
  filter: not(api.tvBundles.initBundlesRowByPageFx.pending),
  fn: (clockData) => ({ ...clockData, count: DEFAULT_ROW_ELEMENTS_COUNT }),
  target: api.tvBundles.initBundlesRowByPageFx,
})
sample({
  clock: load,
  filter: not(api.tv.initInfiniteChannelsCategoryFx.pending),
  fn: (clockData) => ({ ...clockData, count: DEFAULT_ROW_ELEMENTS_COUNT }),
  target: api.tv.initInfiniteChannelsCategoryFx,
})
sample({
  clock: load,
  filter: not(api.tv.initCategoriesWithChannelsFx.pending),
  target: api.tv.initCategoriesWithChannelsFx,
})
sample({
  clock: load,
  filter: not(api.banners.getBannersFx.pending),
  fn: noop,
  target: api.banners.getBannersFx,
})
sample({
  clock: load,
  filter: not(api.banners.getFeaturedCarouselFx.pending),
  fn: noop,
  target: api.banners.getFeaturedCarouselFx,
})

const loadMoreTvCategories = genPagesLogic(
  api.tv.$pageInfoCategoriesWithChannels,
  api.tv.loadCategoriesWithChannelsFx
)

/********** Skeleton logics starts ***********/
const $isAnyInitFxPending = or(
  // TODO should be only attach fx`s there
  api.banners.getBannersFx.pending,
  api.banners.getFeaturedCarouselFx.pending,
  api.tvBundles.initBundlesRowByPageFx.pending,
  api.tv.initInfiniteChannelsCategoryFx.pending,
  api.tv.initCategoriesWithChannelsFx.pending
)

sample({
  clock: $isAnyInitFxPending,
  source: router.$pathname,
  filter: (pathname) => pathname === '/modules/tv_channels',
  fn: (_, isAnyInitFxPending) => isAnyInitFxPending,
  target: setContentLoading,
})

/********** Skeleton logics ends ***********/

const tvCatalogInitsSettled = combineEvents({
  events: {
    banners: api.banners.getBannersFx.finally,
    featured: api.banners.getFeaturedCarouselFx.finally,
    bundles: api.tvBundles.initBundlesRowByPageFx.finally,
    infinite: api.tv.initInfiniteChannelsCategoryFx.finally,
    categories: api.tv.initCategoriesWithChannelsFx.finally,
  },
})

export const $tvCatalogSettled = createStore(false).reset(init)

sample({
  clock: tvCatalogInitsSettled,
  fn: () => true,
  target: $tvCatalogSettled,
})

const $bannerTvCarouselShown = and(
  options.$bannerCarouselsEnabled,
  bannersModel.$bannersTypeTvFormatted.map((banners) =>
    banners.some(({ bannerBundles }) => bannerBundles?.length)
  )
)

const $featuredTvCarouselShown = and(
  options.$featuredCarouselsEnabled,
  tvFeaturedModel.$featuredCarouselTypeTv.map((carousels) =>
    carousels.some(
      (item) => item.enabledOnModulePage && item.content?.length > 0
    )
  )
)

const $anyVisibleTvShelf = combine(
  $bannerTvCarouselShown,
  $featuredTvCarouselShown,
  tvBundlesModel.$tvBundlesRowFormatted,
  tvCategoriesModel.$tvCategoriesWithChannels,
  api.tv.$pageInfoCategoriesWithChannels,
  channelsModel.$infiniteChannelsFormatted,
  (banners, featured, bundles, categories, pageInfo, infinite) =>
    banners ||
    featured ||
    bundles.length > 0 ||
    (categories?.length ?? 0) > 0 ||
    Boolean(pageInfo?.last && infinite?.content?.length)
)

export const $isTvEmptyPage = combine(
  $tvCatalogSettled,
  $anyVisibleTvShelf,
  (settled, anyVisible) => settled && !anyVisible
)

// clear paginatable stores
api.banners.$bannersTypeTv.reset(init)
api.tvBundles.$tvBundlesRow.reset(init)
api.tv.$tvCategoriesWithChannels.reset(init) // not necessary

register(tvModel.seeAllTvClick, 'seeAllTvClick')
register(tvBundlesModel.$tvBundlesRowFormatted, '$tvBundlesRow')
register(tvBundlesModel.$tvBundlesRowTitle, '$tvBundlesRowTitle')
register(bannersModel.$bannersTypeTvFormatted, '$bannersTypeTv')
register($bannerTvCarouselShown, '$bannerTvCarouselShown')
register(tvFeaturedModel.$featuredCarouselTypeTv, '$featuredCarouselTypeTv')
register(tvFeaturedModel.$featuredCarouselTvTitle, '$featuredCarouselTvTitle')
register($featuredTvCarouselShown, '$featuredTvCarouselShown')
register(
  tvCategoriesModel.$tvCategoriesWithChannels,
  '$tvCategoriesWithChannels'
)
register(loadMoreTvCategories, 'loadMoreTvCategories')
register(
  api.tv.$pageInfoCategoriesWithChannels,
  '$pageInfoCategoriesWithChannels'
)
register(channelsModel.$infiniteChannelsFormatted, '$infiniteChannelsFormatted')
register(channelsModel.$infiniteTvTitle, '$infiniteTvTitle')
register($isTvEmptyPage, '$isTvEmptyPage')
